/**
 * @file Barres de vie flottantes au-dessus des cibles destructibles TOUCHEES : un seul InstancedMesh (3 quads par barre : cadre sombre,
 * fond clair, jauge coloree), orientees vers la camera, dessinees par-dessus la scene (1 draw call, aucune allocation par frame).
 */
import { Color, DynamicDrawUsage, InstancedMesh, Matrix4, MeshBasicMaterial, PlaneGeometry, Quaternion, Vector3 } from 'three';
import type { Scene } from 'three';
import type { ToonKit } from '../toon/toon';

/** Barres simultanees maximales (3 instances chacune). */
const CAPACITY = 160;
const BAR_HEIGHT = 0.28;
const FRAME_PAD = 0.1;
const FRAME_COLOR = '#2b2017';
const TRACK_COLOR = '#efe3cf';
/** Couleur de la jauge : vert (plein) -> jaune -> rouge (presque vide). */
const COLOR_FULL = '#6fdc7b';
const COLOR_MID = '#ffd45e';
const COLOR_LOW = '#ff5a5a';
const LAYER_Z = 0.001;

export class HealthBars {
  private readonly mesh: InstancedMesh;
  private count = 0;
  private readonly m = new Matrix4();
  private readonly p = new Vector3();
  private readonly s = new Vector3();
  private readonly right = new Vector3(1, 0, 0);
  private readonly up = new Vector3(0, 1, 0);
  private readonly camQ = new Quaternion();
  private readonly c = new Color();
  private readonly full = new Color(COLOR_FULL);
  private readonly mid = new Color(COLOR_MID);
  private readonly low = new Color(COLOR_LOW);
  private readonly frameC = new Color(FRAME_COLOR);
  private readonly trackC = new Color(TRACK_COLOR);

  constructor(
    private readonly scene: Scene,
    kit: ToonKit,
  ) {
    const geo = kit.track(new PlaneGeometry(1, 1));
    const mat = kit.track(new MeshBasicMaterial({ depthTest: false, depthWrite: false, toneMapped: false }));
    this.mesh = new InstancedMesh(geo, mat, CAPACITY * 3);
    this.mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 20;
    this.mesh.count = 0;
    this.mesh.setColorAt(0, this.frameC); // alloue instanceColor
    scene.add(this.mesh);
  }

  /** Orientation de la camera (les barres lui font face) : a appeler une fois par frame avant les draw(). */
  setCamera(q: Quaternion): void {
    this.camQ.copy(q);
    this.right.set(1, 0, 0).applyQuaternion(q);
    this.up.set(0, 1, 0).applyQuaternion(q);
  }

  begin(): void {
    this.count = 0;
  }

  /** Une barre centree en (x, h, -y) (coordonnees core x, y), `ratio` 0..1, largeur monde `width`. */
  draw(x: number, y: number, h: number, ratio: number, width: number): void {
    if (this.count + 3 > CAPACITY * 3) return;
    const r = Math.min(1, Math.max(0, ratio));
    this.layer(x, y, h, 0, width + FRAME_PAD * 2, BAR_HEIGHT + FRAME_PAD * 2, this.frameC);
    this.layer(x, y, h, LAYER_Z, width, BAR_HEIGHT, this.trackC);
    if (r > 0.001) {
      this.c.copy(r > 0.5 ? this.mid.clone().lerp(this.full, (r - 0.5) * 2) : this.low.clone().lerp(this.mid, r * 2));
      // jauge ancree a gauche : centre decale de -(width - w)/2 le long de l'axe horizontal de l'ecran
      this.layer(x, y, h, LAYER_Z * 2, width * r, BAR_HEIGHT, this.c, -(width - width * r) / 2);
    } else {
      this.layer(x, y, h, LAYER_Z * 2, 0.0001, BAR_HEIGHT, this.low);
    }
  }

  end(): void {
    this.mesh.count = this.count;
    this.mesh.visible = this.count > 0;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  dispose(): void {
    this.scene.remove(this.mesh);
    this.mesh.dispose();
  }

  private layer(x: number, y: number, h: number, lift: number, w: number, hh: number, color: Color, offsetX = 0): void {
    this.p.set(x, h, -y).addScaledVector(this.right, offsetX).addScaledVector(this.up, 0).addScaledVector(this.camDir(), -lift);
    this.s.set(w, hh, 1);
    this.m.compose(this.p, this.camQ, this.s);
    this.mesh.setMatrixAt(this.count, this.m);
    this.mesh.setColorAt(this.count, color);
    this.count++;
  }

  private readonly dir = new Vector3();
  /** Direction camera -> scene (-Z de la camera) : les couches successives sont legerement rapprochees de la camera. */
  private camDir(): Vector3 {
    return this.dir.set(0, 0, -1).applyQuaternion(this.camQ);
  }
}
